import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import Program from "@/pages/Program";
import ExpeditionMap from "@/pages/ExpeditionMap";
import FAQ from "@/pages/FAQ";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import JoinList from "@/pages/JoinList";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import CampsPublic from "@/pages/CampsPublic";
import Daytime from "@/pages/Daytime";
import Support from "@/pages/Support";
import Events, { DashboardEvents } from "@/pages/Events";
import { FirstVisitLocation } from "@/components/FirstVisitLocation";
import SponsorCertificate from "@/pages/support/SponsorCertificate";
import FoundingBadge from "@/pages/support/FoundingBadge";
import DaytimePlay from "@/pages/dashboard/DaytimePlay";
import DayPassBook from "@/pages/dashboard/DayPassBook";
import RockCrew from "@/pages/staff/RockCrew";
import ExtraServices from "@/pages/staff/ExtraServices";

import Dashboard, { Overview } from "@/pages/dashboard/Dashboard";
import Explorers from "@/pages/dashboard/Explorers";
import BookAdventure from "@/pages/dashboard/BookAdventure";
import Wallet from "@/pages/dashboard/Wallet";
import GoldCard from "@/pages/dashboard/GoldCard";
import SnackShop from "@/pages/dashboard/SnackShop";
import { Bookings, Credits } from "@/pages/dashboard/Bookings";
import Camps from "@/pages/dashboard/Camps";
import Dinner from "@/pages/dashboard/Dinner";
import Lunch from "@/pages/dashboard/Lunch";
import DayGallery from "@/pages/dashboard/DayGallery";
import { Messages, Documents, Account } from "@/pages/dashboard/Info";
import Admin from "@/pages/admin/Admin";
import Scanner from "@/pages/staff/Scanner";
import StaffTransport from "@/pages/staff/StaffTransport";
import StaffAttendance from "@/pages/staff/StaffAttendance";
import Watcher from "@/pages/staff/Watcher";
import DriverRoute from "@/pages/staff/DriverRoute";
import StaffProfile from "@/pages/staff/StaffProfile";
import StaffPhotos from "@/pages/staff/StaffPhotos";
import StaffProgram from "@/pages/staff/Program";
import CampRoster from "@/pages/staff/CampRoster";
import TodaysDinners from "@/pages/staff/TodaysDinners";
import TodaysLunches from "@/pages/staff/TodaysLunches";
import RockTalk from "@/pages/staff/RockTalk";
import Party from "@/pages/dashboard/Party";

function Splash() {
  return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-turquoise" /></div>;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/program" element={<Program />} />
      <Route path="/calendar" element={<ExpeditionMap />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/join" element={<JoinList />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/camps" element={<CampsPublic />} />
      <Route path="/daytime" element={<Daytime />} />
      <Route path="/support" element={<Support />} />
      <Route path="/events" element={<Events />} />
      <Route path="/support/certificate/:token" element={<SponsorCertificate />} />
      <Route path="/support/founding-badge/:token" element={<FoundingBadge />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="explorers" element={<Explorers />} />
        <Route path="book" element={<BookAdventure />} />
        <Route path="daytime" element={<DaytimePlay />} />
        <Route path="daytime/book" element={<DayPassBook />} />
        <Route path="events" element={<DashboardEvents />} />
        <Route path="camps" element={<Camps />} />
        <Route path="gallery" element={<DayGallery />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="credits" element={<Credits />} />
        <Route path="gold" element={<GoldCard />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="snacks" element={<SnackShop />} />
        <Route path="dinner" element={<Dinner />} />
        <Route path="lunch" element={<Lunch />} />
        <Route path="rock-talk" element={<RockTalk />} />
        <Route path="party" element={<Party />} />
        <Route path="messages" element={<Messages />} />
        <Route path="documents" element={<Documents />} />
        <Route path="account" element={<Account />} />
      </Route>

      <Route path="/admin" element={<Protected roles={["admin", "manager"]}><Admin /></Protected>} />
      <Route path="/staff" element={<Protected roles={["admin", "manager", "staff"]}><Scanner /></Protected>} />
      <Route path="/staff/extras" element={<Protected roles={["admin", "manager", "staff", "watcher"]}><ExtraServices /></Protected>} />
      <Route path="/staff/daytime" element={<Protected roles={["admin", "manager", "staff", "watcher"]}><RockCrew /></Protected>} />
      <Route path="/staff/transport" element={<Protected roles={["admin", "manager", "staff", "driver"]}><StaffTransport /></Protected>} />
      <Route path="/staff/attendance" element={<Protected roles={["admin", "manager", "staff", "watcher"]}><StaffAttendance /></Protected>} />
      <Route path="/watcher" element={<Protected roles={["admin", "manager", "staff", "watcher"]}><Watcher /></Protected>} />
      <Route path="/driver" element={<Protected roles={["admin", "manager", "staff", "driver"]}><DriverRoute /></Protected>} />
      <Route path="/staff/profile" element={<Protected roles={["admin", "manager", "staff", "watcher", "driver"]}><StaffProfile /></Protected>} />
      <Route path="/staff/photos" element={<Protected roles={["admin", "manager", "staff", "watcher", "driver"]}><StaffPhotos /></Protected>} />
      <Route path="/staff/program" element={<Protected roles={["admin", "manager", "staff", "watcher", "driver"]}><StaffProgram /></Protected>} />
      <Route path="/staff/camp-roster" element={<Protected roles={["admin", "manager", "staff", "driver"]}><CampRoster /></Protected>} />
      <Route path="/staff/dinners" element={<Protected roles={["admin", "manager", "staff"]}><TodaysDinners /></Protected>} />
      <Route path="/staff/lunches" element={<Protected roles={["admin", "manager", "staff"]}><TodaysLunches /></Protected>} />
      <Route path="/staff/rock-talk" element={<Protected roles={["admin", "manager", "staff", "watcher", "driver"]}><RockTalk /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <FirstVisitLocation />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
